import { create } from 'zustand';
import convex from '../convexClient.js';
import { api } from '../../convex/_generated/api';
import {
    buildVisibleProjectFileTree,
    joinProjectFilePath,
    isNestedProjectPath,
    WRITING_STUDIO_SIDEBAR_HIDDEN_FOLDERS,
} from '../../shared/projectFileSystem.ts';

const TAB_STORAGE_KEY = 'share-pen:editor-tabs';

const saveTabState = (projectId, activeTabPath, openTabs) => {
    try {
        const data = JSON.parse(localStorage.getItem(TAB_STORAGE_KEY) || '{}');
        data[projectId] = { activeTabPath, openTabs };
        localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify(data));
    } catch (_) { /* silent */ }
};

const loadTabState = (projectId) => {
    try {
        const data = JSON.parse(localStorage.getItem(TAB_STORAGE_KEY) || '{}');
        return data[projectId] || null;
    } catch (_) { return null; }
};

const uniquePaths = (paths) => [...new Set(paths)];

const getTopLevelMovePaths = (files, paths) => {
    const existingPaths = new Set(files.map((file) => file.path));
    const unique = uniquePaths(paths).filter((path) => existingPaths.has(path));
    return unique.filter((path) => !unique.some((other) => other !== path && isNestedProjectPath(path, other)));
};

const buildMoveMappings = (files, paths, newParentPath) => {
    const fileMap = new Map(files.map((file) => [file.path, file]));
    return getTopLevelMovePaths(files, paths)
        .map((path) => fileMap.get(path))
        .filter(Boolean)
        .map((file) => ({
            from: file.path,
            to: joinProjectFilePath(newParentPath, file.name),
        }))
        .filter((mapping) => mapping.from !== mapping.to)
        .sort((a, b) => b.from.length - a.from.length);
};

const remapPath = (path, mappings) => {
    for (const mapping of mappings) {
        if (path === mapping.from) {
            return mapping.to;
        }
        if (path.startsWith(`${mapping.from}/`)) {
            return `${mapping.to}${path.slice(mapping.from.length)}`;
        }
    }
    return path;
};

const useFileStore = create((set, get) => ({
    // File tree state
    files: [],
    isLoading: false,
    error: null,

    // Open tabs
    openTabs: [],
    activeTabPath: null,

    // Expanded folders in tree
    expandedFolders: new Set(),

    // Version counter to force editor remount when files change
    fileVersion: 0,

    // Internal project ID for tab persistence
    _projectId: null,

    // Load all files for a project
    loadFiles: async (projectId) => {
        set({ isLoading: true, error: null, _projectId: projectId });
        try {
            await convex.mutation(api.files.ensureOrders, { projectId });
            const files = await convex.query(api.files.listByProject, { projectId });
            const filePaths = new Set(files.map((f) => f.path));
            // Restore saved tab state
            const saved = loadTabState(projectId);
            const restoredTabs = saved?.openTabs?.filter((p) => filePaths.has(p)) || [];
            const restoredActive = saved?.activeTabPath && filePaths.has(saved.activeTabPath) ? saved.activeTabPath : null;
            set((state) => ({
                files,
                isLoading: false,
                fileVersion: state.fileVersion + 1,
                // Only restore if no tabs are open yet (first load / refresh)
                ...(state.openTabs.length === 0 && restoredTabs.length > 0
                    ? { openTabs: restoredTabs, activeTabPath: restoredActive || restoredTabs[0] }
                    : {}),
            }));
        } catch (error) {
            set({ error: error.message, isLoading: false });
        }
    },

    // Folders hidden from the Writing Studio sidebar (managed by their own studio pages)
    HIDDEN_SIDEBAR_FOLDERS: [...WRITING_STUDIO_SIDEBAR_HIDDEN_FOLDERS],

    // Build tree structure from flat files
    getFileTree: () => {
        const files = get().files;
        const hiddenFolders = get().HIDDEN_SIDEBAR_FOLDERS;
        return buildVisibleProjectFileTree(files, hiddenFolders);
    },

    // Create a new file
    createNewFile: async (projectId, parentPath, name, type = 'file') => {
        const path = joinProjectFilePath(parentPath, name);
        const content = type === 'file' ? '' : undefined;

        try {
            const id = await convex.mutation(api.files.upsert, {
                projectId,
                path,
                name,
                type,
                parentPath,
                content
            });

            // Reload files
            await get().loadFiles(projectId);

            // If file, open it in a tab
            if (type === 'file') {
                get().openFile(path);
            }

            // Expand parent folder
            if (parentPath) {
                get().toggleFolder(parentPath, true);
            }

            return id;
        } catch (error) {
            set({ error: error.message });
            return null;
        }
    },

    // Update file content
    updateFileContent: async (projectId, path, content) => {
        const file = get().files.find((f) => f.path === path);
        if (file) {
            await convex.mutation(api.files.updateContent, { projectId, path, content });

            if (path === 'Notes/story-bible.md') {
                await convex.mutation(api.storyBibles.syncFromMarkdown, {
                    projectId,
                    markdown: content,
                });
            }

            // Update in local state
            set((state) => ({
                files: state.files.map(f =>
                    f.path === path ? { ...f, content } : f
                )
            }));
        }
    },

    // Delete files or folders
    deleteItems: async (projectId, paths) => {
        const targets = getTopLevelMovePaths(get().files, paths);
        if (targets.length === 0) {
            return;
        }

        try {
            for (const path of targets) {
                await convex.mutation(api.files.deleteByPathPrefix, {
                    projectId,
                    pathPrefix: path
                });
            }

            set((state) => {
                const nextTabs = state.openTabs.filter((tabPath) =>
                    !targets.some((target) => tabPath === target || tabPath.startsWith(target + '/'))
                );
                const activeRemoved = !state.activeTabPath || targets.some(
                    (target) => state.activeTabPath === target || state.activeTabPath.startsWith(target + '/')
                );
                return {
                    openTabs: nextTabs,
                    activeTabPath: activeRemoved ? nextTabs[0] || null : state.activeTabPath
                };
            });
            const { activeTabPath, openTabs } = get();
            saveTabState(projectId, activeTabPath, openTabs);

            await get().loadFiles(projectId);
        } catch (error) {
            set({ error: error.message });
        }
    },

    // Delete a file or folder
    deleteItem: async (projectId, path) => {
        await get().deleteItems(projectId, [path]);
    },

    // Rename a file or folder
    renameItem: async (projectId, oldPath, newName) => {
        const file = get().files.find(f => f.path === oldPath);
        if (!file) return;

        const newPath = joinProjectFilePath(file.parentPath || '', newName);

        try {
            await convex.mutation(api.files.rename, {
                projectId,
                oldPath,
                newPath,
                newName
            });

            // Update open tabs
            set((state) => ({
                openTabs: state.openTabs.map(t => {
                    if (t === oldPath) return newPath;
                    if (t.startsWith(oldPath + '/')) return t.replace(oldPath, newPath);
                    return t;
                }),
                activeTabPath: state.activeTabPath === oldPath ? newPath
                    : state.activeTabPath?.startsWith(oldPath + '/')
                        ? state.activeTabPath.replace(oldPath, newPath)
                        : state.activeTabPath
            }));

            await get().loadFiles(projectId);
        } catch (error) {
            set({ error: error.message });
        }
    },

    moveItems: async (projectId, itemPaths, newParentPath) => {
        const mappings = buildMoveMappings(get().files, itemPaths, newParentPath);
        if (mappings.length === 0) {
            return [];
        }

        try {
            await convex.mutation(api.files.moveMany, {
                projectId,
                itemPaths: mappings.map((mapping) => mapping.from),
                destinationParentPath: newParentPath,
            });
        } catch (error) {
            console.error('[moveItems] moveMany failed, falling back to rename:', error);
            // Fallback: move items one by one using rename
            for (const mapping of mappings) {
                try {
                    const file = get().files.find((f) => f.path === mapping.from);
                    if (!file) continue;
                    await convex.mutation(api.files.rename, {
                        projectId,
                        oldPath: mapping.from,
                        newPath: mapping.to,
                        newName: file.name,
                    });
                } catch (renameError) {
                    console.error('[moveItems] rename fallback also failed:', renameError);
                    set({ error: renameError.message });
                    return [];
                }
            }
        }

        set((state) => ({
            openTabs: state.openTabs.map((path) => remapPath(path, mappings)),
            activeTabPath: state.activeTabPath ? remapPath(state.activeTabPath, mappings) : null,
        }));

        await get().loadFiles(projectId);
        if (newParentPath) {
            get().toggleFolder(newParentPath, true);
        }
        return mappings;
    },

    // Move a file or folder to a new parent path
    moveItem: async (projectId, oldPath, newParentPath) => {
        return await get().moveItems(projectId, [oldPath], newParentPath);
    },

    reorderItems: async (projectId, parentPath, orderedPaths) => {
        const orderMap = new Map(orderedPaths.map((path, index) => [path, index]));
        set((state) => ({
            files: state.files.map((file) => {
                if ((file.parentPath || '') !== parentPath) {
                    return file;
                }
                const nextOrder = orderMap.get(file.path);
                return typeof nextOrder === 'number' ? { ...file, order: nextOrder } : file;
            })
        }));

        try {
            await convex.mutation(api.files.reorderWithinParent, {
                projectId,
                parentPath,
                orderedPaths
            });
        } catch (error) {
            console.error('[reorderItems] FAILED:', error.message || error);
            set({ error: error.message });
            await get().loadFiles(projectId);
        }
    },

    // Tab management
    openFile: (path) => {
        set((state) => {
            const newTabs = state.openTabs.includes(path)
                ? state.openTabs
                : [...state.openTabs, path];
            return { openTabs: newTabs, activeTabPath: path };
        });
        // Persist to localStorage
        const { activeTabPath, openTabs, _projectId } = get();
        if (_projectId) saveTabState(_projectId, activeTabPath, openTabs);
    },

    closeTab: (path) => {
        set((state) => {
            const newTabs = state.openTabs.filter(t => t !== path);
            let newActive = state.activeTabPath;

            if (state.activeTabPath === path) {
                const idx = state.openTabs.indexOf(path);
                newActive = newTabs[Math.min(idx, newTabs.length - 1)] || null;
            }

            return { openTabs: newTabs, activeTabPath: newActive };
        });
        const { activeTabPath, openTabs, _projectId } = get();
        if (_projectId) saveTabState(_projectId, activeTabPath, openTabs);
    },

    setActiveTab: (path) => {
        set({ activeTabPath: path });
        const { openTabs, _projectId } = get();
        if (_projectId) saveTabState(_projectId, path, openTabs);
    },

    // Folder expansion
    toggleFolder: (path, forceOpen = null) => {
        set((state) => {
            const newExpanded = new Set(state.expandedFolders);
            if (forceOpen === true) {
                newExpanded.add(path);
            } else if (forceOpen === false) {
                newExpanded.delete(path);
            } else if (newExpanded.has(path)) {
                newExpanded.delete(path);
            } else {
                newExpanded.add(path);
            }
            return { expandedFolders: newExpanded };
        });
    },

    isFolderExpanded: (path) => {
        return get().expandedFolders.has(path);
    },

    // Get file content by path
    getFileContent: (path) => {
        const file = get().files.find(f => f.path === path);
        return file?.content || '';
    },

    // Check if file has unsaved changes (for tab indicator)
    unsavedFiles: new Set(),

    markUnsaved: (path) => {
        set((state) => {
            const newUnsaved = new Set(state.unsavedFiles);
            newUnsaved.add(path);
            return { unsavedFiles: newUnsaved };
        });
    },

    markSaved: (path) => {
        set((state) => {
            const newUnsaved = new Set(state.unsavedFiles);
            newUnsaved.delete(path);
            return { unsavedFiles: newUnsaved };
        });
    },

    isUnsaved: (path) => {
        return get().unsavedFiles.has(path);
    },

    // Reset state
    reset: () => {
        set({
            files: [],
            openTabs: [],
            activeTabPath: null,
            expandedFolders: new Set(),
            unsavedFiles: new Set(),
            isLoading: false,
            error: null
        });
    }
}));

export default useFileStore;
